/**
 * Provides helper functions for creating and manipuating esri/layers/GraphicsLayer
 */
window.GraphicsLayerHelper = {
    /**
     * Create a GraphicsLayer
     */
    createGraphicsLayer(layerId) {
        if (!layerId) {
            return;
        }

        return this.modules.GraphicsLayer({ id: layerId });
    },

    /**
     * Add a GraphicsLayer to the map. Default layerIndex = 0 and the layer will be added above all layer
     */
    addGraphicsLayer(layerId, layerIndex = 0) {
        const layer = this.createGraphicsLayer(layerId);
        this.map.add(layer, layerIndex);
        if (!layer) {
            return;
        }

        return true;
    },

    /**
     * Set visibility of the GraphicsLayer's graphic
     */
    graphicsLayerUpdateVisibility(layerId, featureId, visibility) {
        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        const graphics = this.graphicsLayerGetGraphics(layerId, featureId);

        graphics.forEach(graphic => {
            if (graphic.visible !== visibility) {
                graphic.visible = visibility;
            }
        });
    },

    /**
     *  Add graphics to a GraphicsLayer
     */
    graphicsLayerAddGraphics(layerId, graphics) {
        if (!layerId || !graphics) {
            return;
        }

        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        if (Array.isArray(graphics)) {
            const _graphics = graphics.filter(g => g);
            layer && layer.addMany(_graphics);
        } else {
            layer && layer.addMany([graphics]);
        }
    },

    /**
     *  Remove graphics from a GraphicsLayer
     */
    graphicsLayerRemoveGraphic(layerId, featureId) {
        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }

        const graphics = this.graphicsLayerGetGraphics(layerId, featureId);
        if (!graphics || graphics.length === 0) {
            return;
        }

        Array.isArray(graphics) ? layer.removeMany(graphics) : layer.remove(graphics);

        if (this.view.popup && this.view.popup.visible) {
            this.view.popup.close();
        }
    },

    /**
     * Remove all grahics from the GraphicsLayer
     */
    graphicsLayerRemoveAllGraphics(layerId) {
        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return;
        }
        layer.removeAll();

        if (
            this.view.popup &&
            this.view.popup.visible &&
            this.view.popup.featureCount > 0 &&
            this.view.popup.features.some(feature => feature.layer.id === layerId)
        ) {
            this.view.popup.close();
        }
    },

    /**
     * Get a graphic from the GraphicsLayer
     */
    graphicsLayerGetGraphic(layerId, featureId) {
        const graphics = this.graphicsLayerGetGraphics(layerId, featureId);
        if (graphics && graphics.length > 0) {
            return graphics[0];
        }
    },

    /**
     * Get an array of graphics from the GraphicsLayer
     */
    graphicsLayerGetGraphics(layerId, featureId) {
        if (!layerId || !featureId) {
            return [];
        }

        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return [];
        }

        /*
            Due to BUG-000128010 in ArcGIS JS v4.14 (https://developers.arcgis.com/javascript/latest/guide/release-notes/),
            filtering GraphicsLayer features will remove the filter result from map.
            A temporary solution was added in DIS-2868 to work around the problem.
            The ArcGIS bug was fixed in v4.15. When upgrading ArcGIS again please revert the workaround
        */

        const filterResult = [];
        layer.graphics.forEach(graphic => {
            if (
                graphic &&
                graphic.attributes &&
                (graphic.attributes.id === featureId || graphic.attributes.Id === featureId)
            ) {
                filterResult.push(graphic);
            }
        });
        return filterResult;
    },

    /**
     * Add a polygon graphic to the layer using a color-filled symbol
     */
    addCircleGraphic(layerId, featureId, geometry, symbol, popup) {
        if (!geometry || !geometry.radius || !geometry.center || !symbol) {
            return;
        }

        const _geometry = Object.assign(geometry, { type: 'circle', geodesic: true });
        const attributes = { id: featureId };
        const _symbol = Object.assign(symbol, { type: 'simple-fill' });

        const graphic = this.createCircleGraphic(_geometry, attributes, _symbol, popup);
        this.graphicsLayerAddGraphics(layerId, graphic);
    },

    /*
     * Get all graphics in the give layer
     */
    async graphicsLayerGetAllGraphics(layerId) {
        if (!layerId) {
            return [];
        }

        const layer = this.map.findLayerById(layerId);
        if (!layer) {
            return [];
        }

        try {
            const layerView = await this.view.whenLayerView(layer);
            const featureSet = await layerView.queryGraphics();
            return featureSet.items;
        } catch (_0) {
            return [];
        }
    },

    /**
     * Get the GraphicsLayer graphics which intersect with the input polygon
     */
    async graphicsLayerIntersectsQuery(layerId, polygon) {
        const graphics = await this.graphicsLayerGetAllGraphics(layerId);

        if (graphics.length === 0 || !polygon || polygon.rings.length === 0) {
            return [];
        }

        // geometries of graphics are in Geographic sp. ref
        // and we need to convert polygon's sp. ref to match the graphics' geometry
        // otherwise the following intersects check will return false
        if (polygon.spatialReference.isWebMercator) {
            polygon = this.modules.webMercatorUtils.webMercatorToGeographic(polygon);
        }

        return this.filterAsync(
            graphics,
            async graphic =>
                await this.modules.geometryEngineAsync.intersects(polygon, graphic.geometry)
        );
    },

    async filterAsync(items, filterFunction) {
        const filterResults = await Promise.all(items.map(filterFunction));
        return items.filter((_0, index) => filterResults[index]);
    }
};
